import Handlebars from 'handlebars/runtime';
import lang from '../../locales/en.json';
import { formatPrice } from '../seller/lib/price-format';
import { LocalQuote } from '../seller/local-quote';

/* Default to english */
Handlebars.translation = lang;
Handlebars.closedStates = ['declined', 'canceled', 'purchased'];

/**
 * Translation Pattern Map
 * Maps pattern types to translation key builders.
 * Single source of truth for dynamic translation key generation.
 * 
 * @example
 * TRANSLATION_PATTERN_MAP['share_medium_message']('whatsapp') => 'share.whatsapp_message'
 * TRANSLATION_PATTERN_MAP['share_medium_subject']('email') => 'share.email_subject'
 */
export const TRANSLATION_PATTERN_MAP = {
    'share_medium_message': (val) => `share.${val}_message`,
    'share_medium_subject': (val) => `share.${val}_subject`
};

Handlebars.registerHelper('toCurrency', function (options) {
    const config = options.hash.config;
    const value = options.hash.value;

    return formatPrice(value, config);
});

Handlebars.registerHelper('eachKeyValue', function (context, options) {
    let result = '';
    for (let key in context) {
        if (context.hasOwnProperty(key)) {
            result += options.fn({ key: key, value: context[key] });
        }
    }
    return result;
});

Handlebars.registerHelper('isCartWildcard', function (arg1, options) {
    return arg1 === LocalQuote.WILDCARD;
});

Handlebars.registerHelper('and', function (...args) {
  const options = args.pop(); // the last argument is the options object
  return args.every(Boolean) ? options.fn(this) : (options.inverse ? options.inverse(this) : '');
});

Handlebars.registerHelper('not', function (value) {
  return !value;
});

Handlebars.registerHelper('toHtml', function(text) {
    function htmlDecode(input) {
        var doc = new DOMParser().parseFromString(input, "text/html");
        return doc.documentElement.textContent;
    }

    // Decode HTML entities sent by Ahia
    let _text = htmlDecode(String(text || ''));
    // Replace newlines with <br> tags
    _text = _text.replace(/(?:\r\n|\r|\n)/g, '<br>');
    return new Handlebars.SafeString(_text);
});

Handlebars.registerHelper('find', function(array, prop, value, options) {

    // Check if the array is valid and prop is provided
    if (!Array.isArray(array) || typeof prop === 'undefined') {
      return options.inverse ? options.inverse(this) : '';
    }
  
    // Find the first object with matching property and value
    const found = array.find(obj => obj[prop] === value);
  
    // Render the block with the found object or inverse block
    if (found) {
      return options.fn(found);
    } else {
      return options.inverse ? options.inverse(this) : '';
    }
  });

Handlebars.registerHelper('eq', function (arg1, arg2, options) {
    const result = arg1 == arg2;
    
    // If used as a block helper (has fn function)
    if (options && typeof options.fn === 'function') {
        return result ? options.fn(this) : (options.inverse ? options.inverse(this) : '');
    }
    
    // If used as inline helper (within other helpers)
    return result;
});

Handlebars.registerHelper('neq', function (arg1, arg2, options) {
    const result = arg1 != arg2;
    
    // If used as a block helper (has fn function)
    if (options && typeof options.fn === 'function') {
        return result ? options.fn(this) : (options.inverse ? options.inverse(this) : '');
    }
    
    // If used as inline helper (within other helpers)
    return result;
});

Handlebars.registerHelper("lessThan", function (a, b, options) {
    const result = a < b;
    
    // If used as a block helper (has fn function)
    if (options && typeof options.fn === 'function') {
        return result ? options.fn(this) : (options.inverse ? options.inverse(this) : '');
    }
    
    // If used as inline helper (within other helpers)
    return result;
  });

Handlebars.registerHelper('get', function (objectArray, key) {
    // Support string, number, or context-based keys
    const resolvedKey =
        typeof key === 'string'
            ? this[key] || key
            : typeof key === 'number'
              ? key
              : null;

    // If objectArray is an array, find the first object with the key
    if (Array.isArray(objectArray)) {
        const foundObj = objectArray.find((obj) => resolvedKey in obj);
        return foundObj ? foundObj[resolvedKey] : undefined;
    }

    return objectArray && objectArray[resolvedKey];
});

Handlebars.registerHelper("anyComparison", function(operator, array, field, value, options) {
  if (!Array.isArray(array)) {
    return options.inverse ? options.inverse(this) : '';
  }

  const ops = {
    "==":  (a, b) => a == b,
    "===": (a, b) => a === b,
    "!=":  (a, b) => a != b,
    "!==": (a, b) => a !== b,
    ">":   (a, b) => a > b,
    ">=":  (a, b) => a >= b,
    "<":   (a, b) => a < b,
    "<=":  (a, b) => a <= b,
  };

  const cmp = ops[operator];
  if (!cmp) throw new Error(`Unsupported operator "${operator}"`);

  const hasMatch = array.some(item => cmp(item[field], value));

  return hasMatch ? options.fn(this) : (options.inverse ? options.inverse(this) : '');
});

Handlebars.registerHelper("cmp", function (operator, left, right, options) {
  const ops = {
    "==":  (a, b) => a == b,
    "===": (a, b) => a === b,
    "!=":  (a, b) => a != b,
    "!==": (a, b) => a !== b,
    ">":   (a, b) => a > b,
    ">=":  (a, b) => a >= b,
    "<":   (a, b) => a < b,
    "<=":  (a, b) => a <= b,
  };

  const cmp = ops[operator];
  if (!cmp) throw new Error(`Unsupported operator "${operator}"`);

  const result = cmp(left, right);

  // Block usage (with {{else}})
  if (options && typeof options.fn === "function") {
    return result ? options.fn(this) : (options.inverse ? options.inverse(this) : '');
  }

  // Inline usage: {{cmp ">" a b}}
  return result;
});

Handlebars.registerHelper("inArray", function(value, array, options) {
  // If used as a block helper
  if (options && typeof options.fn === 'function') {
    if (Array.isArray(array) && array.includes(value)) {
        return options.fn(this);
    }
    return options.inverse ? options.inverse(this) : '';
  }
  // If used as a simple helper (returns boolean)
  return Array.isArray(array) && array.includes(value);
});

Handlebars.registerHelper('hasItems', function (collection) {
    if (Array.isArray(collection) && collection.length > 0) {
        return true;
    }
    // if its an object, check if it has keys
    if (typeof collection === 'object' && collection !== null) {
        return Object.keys(collection).length > 0;
    }
});

Handlebars.registerHelper('isValidEmail', function (email) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
});

Handlebars.registerHelper('isEven', function (value) {
    return value % 2 === 0;
});

Handlebars.registerHelper("for", function (from, to, options) {
    let result = "";
    for (let i = from; i <= to; i++) {
      result += options.fn(i); 
    }
    return result;
});

Handlebars.registerHelper("toStar", function(stars, options) {
    const asIcon = options.hash.asIcon;
    const starSymbol = asIcon ? '<i class="fa-solid fa-star"></i>' : '★';
    return stars.replace(/\*/g, starSymbol);
});

Handlebars.registerHelper('getImg', function (value) {
    // const isProduction = process.env.NODE_ENV === "production";
    const currentPath = __webpack_public_path__;
    const url = `${currentPath}/images/${value}`;

    return url;
});

Handlebars.registerHelper('split', function(str, delimiter) {
  if (typeof str !== 'string') return [];
  return str.split(delimiter);
});

Handlebars.registerHelper('eqBool', function(a, b) {
  return a === b;
});

Handlebars.registerHelper('split', function(str, delimiter) {
  if (typeof str !== 'string') return [];
  return str.split(delimiter);
});

Handlebars.registerHelper('orBool', function() {
  for (let i = 0; i < arguments.length - 1; i++) {
    if (arguments[i]) return true;
  }
  return false;
});

Handlebars.registerHelper('andBool', function(...args) {
    // The last argument from Handlebars is the options object; we can ignore it.
    args.pop();
    return args.every(Boolean);
});

Handlebars.registerHelper('repeat', function(count, options) {
    let result = '';
    for (let i = 0; i < count; i++) {
        result += options ? options.fn(this) : '!';
    }
    return new Handlebars.SafeString(result);
});

// Define a function that retrieves and processes the translation.
Handlebars.applyTranslation = function(key, ...params) {
  // If the last parameter is a Handlebars options object, remove it.
  if (params.length && params[params.length - 1] && params[params.length - 1].hash !== undefined) {
    params.pop();
  }

  // Traverse nested keys to get the translation string.
  const keys = key.split('.');
  let translation = Handlebars.translation;
  for (let i = 0; i < keys.length; i++) {
    if (translation) {
      translation = translation[keys[i]];
    }
  }

  // If translation is not found or not a string, return an empty safe string.
  if (typeof translation !== 'string') {
    return new Handlebars.SafeString('');
  }

  // Replace placeholders in the translation string.
  // Placeholders are in the form {{1}}, {{2}}, etc.
  params.forEach((param, index) => {
    const placeholder = new RegExp('\\{\\{' + (index + 1) + '\\}\\}', 'g');
    translation = translation.replace(placeholder, param);
  });

  return new Handlebars.SafeString(translation);
};

// Register the Handlebars helper 't' that delegates to applyTranslation.
Handlebars.registerHelper('t', function(key, ...params) {
  return Handlebars.applyTranslation(key, ...params);
});

// Utility function to get translation key based on type and value
// Can be used both in Handlebars templates and JavaScript code
export const getTranslationByPattern = (type, value) => {
  if (!type || !value) {
    return null;
  }

  const keyBuilder = TRANSLATION_PATTERN_MAP[type];
  if (!keyBuilder) {
    return null;
  }

  const translationKey = keyBuilder(value);
  
  // Traverse nested keys to get the translation string
  const keys = translationKey.split('.');
  let translation = Handlebars.translation;
  for (let i = 0; i < keys.length; i++) {
    if (translation) {
      translation = translation[keys[i]];
    }
  }

  // If translation is not found or not a string, return null
  if (typeof translation !== 'string') {
    return null;
  }

  return translation;
};

// Helper to pick a translation based on type and value
// Example: {{pickTranslation 'share_medium_message' 'whatsapp'}} -> share.whatsapp_message
// Example: {{pickTranslation 'share_medium_subject' 'email'}} -> share.email_subject
Handlebars.registerHelper('pickTranslation', function(type, value) {
  const translation = getTranslationByPattern(type, value);
  return new Handlebars.SafeString(translation || '');
});

Handlebars.registerHelper('all', function (...args) {
    // Extract the options object (the last argument)
    const options = args.pop();
    // Check if all conditions are truthy
    if (args.every(Boolean)) {
        return options.fn(this);
    } else {
        return options.inverse ? options.inverse(this) : '';
    }
});

Handlebars.registerHelper('or', function (...args) {
    // Last argument is the Handlebars options object
    const options = args.pop();
    const result = args.some(Boolean);
    return result ? options.fn(this) : (options.inverse ? options.inverse(this) : '');
});

export default Handlebars;
